import { cva } from 'class-variance-authority'
import * as React from 'react'

import { cn } from '#app/utils/misc.tsx'
import { Button } from './button.tsx'
import { Icon } from './icon.tsx'
import { Tab, TabGroup } from './tab.tsx'
import { type IconName } from '@/icon-name'

// Tipos para los tabs del navbar
export interface NavTab {
	id: string
	label: string
	href?: string
	isActive?: boolean
	icon?: IconName
	disabled?: boolean
}

// Props del NavBar
export interface NavBarProps {
	/**
	 * Logo - puede ser texto, imagen o componente React
	 */
	logo?: React.ReactNode
	/**
	 * Lista de tabs para la navegación central
	 */
	tabs?: NavTab[]
	/**
	 * Callback cuando se hace click en un tab
	 */
	onTabClick?: (tabId: string) => void
	/**
	 * Props del botón de la derecha
	 */
	button?: {
		label: string
		onClick: () => void
		iconL?: IconName
		iconR?: IconName
		variant?: 'primary' | 'secondary'
		appearance?: 'solid' | 'ghost' | 'text' | 'outline'
	}
	/**
	 * Props para el menú mobile
	 */
	mobileMenu?: {
		isOpen: boolean
		onToggle: () => void
		onClose: () => void
	}
	/**
	 * Clase CSS adicional para el contenedor
	 */
	className?: string
}

// Variantes para el container principal del navbar
const navbarVariants = cva([
	// Layout horizontal con espaciado distribuido
	'flex items-center justify-between',
	// Dimensiones y padding responsive - Exacto según Figma mobile
	'w-full px-2 py-3 md:mx-auto md:max-w-[1440px] md:px-8',
	// Estilo visual - fondo blanco con border radius según Figma
	'rounded-[64px] bg-white',
	// Altura mínima para consistencia
	'min-h-[72px]',
])

// Variantes para el menú mobile
const mobileMenuVariants = cva([
	// Posicionamiento absoluto
	'absolute top-full right-0 left-0 z-50 mt-2',
	// Estilo visual según Figma
	'rounded-[24px] bg-white shadow-[0px_48px_80px_-12px_#d9d9d9]',
	// Padding interno
	'p-8',
	// Animación
	'transition-all duration-300 ease-in-out',
])

// Variantes para items del menú mobile
const mobileMenuItemVariants = cva([
	// Layout
	'flex w-full items-start justify-start px-6 py-4',
	// Estilo visual
	'rounded-[12px] border-0 bg-white',
	// Tipografía
	'font-parkinsans text-[16px] leading-[20px] font-medium text-[#05a18e]',
	// Interacción
	'transition-colors duration-200 hover:bg-gray-50',
])

// Variantes para el botón principal mobile
const mobilePrimaryButtonVariants = cva([
	// Layout
	'flex w-full items-center justify-center px-6 py-4',
	// Estilo visual
	'rounded-[12px] bg-[#05a18e]',
	// Tipografía
	'font-parkinsans text-center text-[16px] leading-[20px] font-medium text-white',
	// Interacción
	'transition-colors duration-200 hover:bg-[#048c7a] active:bg-[#037067]',
])

const NavBar = React.forwardRef<HTMLElement, NavBarProps>(
	(
		{ logo, tabs = [], onTabClick, button, mobileMenu, className, ...props },
		ref,
	) => {
		// Get the currently active tab ID - memoizado para mejor performance
		const activeTabId = React.useMemo(
			() => tabs.find((tab) => tab.isActive)?.id,
			[tabs],
		)

		const handleTabClick = React.useCallback(
			(tabId: string) => {
				// Solo llamar al callback, la navegación se maneja en el componente padre
				onTabClick?.(tabId)
			},
			[onTabClick],
		)

		return (
			<div className="relative">
				<nav ref={ref} className={cn(navbarVariants(), className)} {...props}>
					{/* Logo - Lado izquierdo */}
					<div className="flex items-center">
						{logo && <div className="flex items-center">{logo}</div>}
					</div>

					{/* Tabs - Centro (hidden on mobile) */}
					{tabs.length > 0 && (
						<div className="hidden items-center md:flex">
							{/* Frame container para los tabs - Matching Figma specs */}
							<div
								className={cn(
									'inline-flex items-center rounded-[64px]',
									'bg-white/80 p-1',
								)}
							>
								<TabGroup
									selectedTab={activeTabId}
									onTabChange={handleTabClick}
									orientation="horizontal"
									alignment="center"
								>
									{tabs.map((tab) => (
										<Tab
											className="cursor-pointer"
											key={tab.id}
											id={tab.id}
											icon={tab.icon}
											disabled={tab.disabled}
											state={tab.isActive ? 'selected' : 'default'}
										>
											{tab.label}
										</Tab>
									))}
								</TabGroup>
							</div>
						</div>
					)}

					{/* Desktop Button - Lado derecho (hidden on mobile) */}
					<div className="hidden items-center md:flex">
						{button && (
							<Button
								variant={button.variant || 'secondary'}
								appearance={button.appearance || 'solid'}
								size="l"
								onClick={button.onClick}
							>
								{button.iconL && <Icon name={button.iconL} size="sm" />}
								{button.label}
								{button.iconR && <Icon name={button.iconR} size="sm" />}
							</Button>
						)}
					</div>

					{/* Mobile Menu Button - Solo visible en mobile */}
					{mobileMenu && (
						<div className="flex items-center md:hidden">
							<button
								className="flex items-center justify-center rounded-[48px] p-3 transition-colors duration-200 hover:bg-gray-50"
								onClick={mobileMenu.onToggle}
								aria-label="Abrir menú"
							>
								<Icon name="menu-01" size="md" />
							</button>
						</div>
					)}
				</nav>

				{/* Mobile Menu - Desplegable */}
				{mobileMenu && mobileMenu.isOpen && (
					<>
						{/* Backdrop */}
						<div
							className="fixed inset-0 z-40 bg-black/20 md:hidden"
							onClick={mobileMenu.onClose}
						/>

						{/* Menu Content */}
						<div className={cn(mobileMenuVariants(), 'md:hidden')}>
							<div className="flex flex-col gap-3">
								{/* Navigation Items */}
								<div className="flex flex-col gap-1">
									{tabs.map((tab) => (
										<button
											key={tab.id}
											className={cn(mobileMenuItemVariants())}
											onClick={() => {
												handleTabClick(tab.id)
												mobileMenu.onClose()
											}}
											disabled={tab.disabled}
										>
											{tab.label}
										</button>
									))}
								</div>

								{/* Primary Action Button */}
								{button && (
									<button
										className={cn(mobilePrimaryButtonVariants())}
										onClick={() => {
											button.onClick()
											mobileMenu.onClose()
										}}
									>
										{button.label}
									</button>
								)}
							</div>
						</div>
					</>
				)}
			</div>
		)
	},
)

NavBar.displayName = 'NavBar'

export {
	NavBar,
	navbarVariants,
	mobileMenuVariants,
	mobileMenuItemVariants,
	mobilePrimaryButtonVariants,
}
